import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Whisper-backed dictation button.
 *
 * Tap → start recording (browser MediaRecorder, no Web Speech API beep).
 * Tap again → stop, upload audio to /api/transcribe/audio, append the
 * returned text to the field.
 *
 * Falls back to null silently if MediaRecorder or getUserMedia is
 * unavailable so the form still works.
 */
export default function MicDictation({ value, onTranscript, testId, title = "Dictate", onActivate }) {
  const canRecord =
    typeof window !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== "undefined";

  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const valueRef = useRef(value || "");
  useEffect(() => { valueRef.current = value || ""; }, [value]);

  useEffect(() => {
    return () => {
      try { mediaRecRef.current?.stop(); } catch { /* noop */ }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (!canRecord) return null;

  const pickMime = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    for (const m of candidates) {
      if (window.MediaRecorder.isTypeSupported?.(m)) return m;
    }
    return "";
  };

  const start = async () => {
    if (recording || busy) return;
    // Fire the activation hook BEFORE we open the mic so callers (e.g.
    // Chat OS) can pause the global background music in the same user
    // gesture — otherwise the song bleeds into the first ~200 ms of
    // the recording while React schedules state updates.
    try { onActivate?.(); } catch { /* noop */ }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = mime
        ? new window.MediaRecorder(stream, { mimeType: mime })
        : new window.MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try {
          const blobType = rec.mimeType || "audio/webm";
          const blob = new Blob(chunksRef.current, { type: blobType });
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          if (blob.size < 800) {
            setBusy(false);
            return; // too short — ignore silently
          }
          setBusy(true);
          const ext = blobType.includes("mp4") ? "mp4"
            : blobType.includes("ogg") ? "ogg"
            : "webm";
          const form = new FormData();
          form.append("file", blob, `dictation.${ext}`);
          const { data } = await axios.post(`${API}/transcribe/audio`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const text = ((data && data.text) || "").trim();
          if (text) {
            const base = valueRef.current || "";
            const joiner = base && !base.endsWith(" ") ? " " : "";
            onTranscript((base + joiner + text).trim());
          }
        } catch {
          // Surface silently — button returns to idle so user can retry.
        } finally {
          setBusy(false);
        }
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      // Permission denied or no device — leave mic idle.
      setRecording(false);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const stop = () => {
    if (!recording) return;
    try { mediaRecRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const toggle = () => (recording ? stop() : start());

  const active = recording || busy;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={recording ? "Stop dictating" : busy ? "Transcribing" : "Dictate"}
      title={recording ? "Stop" : busy ? "Transcribing…" : title}
      data-testid={testId}
      data-listening={recording ? "true" : "false"}
      data-busy={busy ? "true" : "false"}
      disabled={busy}
      className={
        "absolute top-2 right-2 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-300 " +
        (active
          ? "border-[#FFE7A0] bg-[#FFE7A0]/15 text-[#FFE7A0] shadow-[0_0_14px_rgba(255, 231, 160,0.55)] " + (recording ? "animate-pulse" : "")
          : "border-[#FFE7A0]/30 bg-[#050818]/40 text-[#FFE7A0]/70 hover:text-[#FFE7A0] hover:border-[#FFE7A0]/60 hover:bg-[#FFE7A0]/10")
      }
    >
      {busy ? (
        // Spinner while Whisper is transcribing
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" />
        </svg>
      ) : recording ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0014 0" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          <path d="M12 18v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0014 0" />
          <path d="M12 18v3" />
          <path d="M8 21h8" />
        </svg>
      )}
    </button>
  );
}
